import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { useFormik } from "formik";
import {
  Snackbar,
  IconButton,
  CircularProgress,
  ThemeProvider,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import SendbirdChat from "@sendbird/chat";
import { GroupChannelModule } from "@sendbird/chat/groupChannel";
import Cookies from "js-cookie";

import Logo from "../../../assets/icons/olinlogo.png";
import Input from "../../common/particles.jsx/Input";
import Carousel from "../../common/particles.jsx/Carousel";
import HOC from "../../HOC";
import { useDispatch } from "react-redux";
import { logout, setUser } from "../../../redux/features/auth/authSlice";
import { checkStatusByEmail, login } from "../../../services/authApi";
import RegisterModal from "./RegisterModal";
import ModalSelectOutlet from "./ModalSelectOutlet";
import { SIDEBAR_URL } from "../../constants/constant";
import { theme } from "../../constants/muiTheme";

const EmailLogin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const userId = localStorage.getItem("company_id");
  const fullname = localStorage.getItem("company_name");
  const profileImg = localStorage.getItem("img_profile");

  const [statusCode, setStatusCode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [passwordVisibility, setPasswordVisibility] = useState(true);
  const [openRegister, setOpenRegister] = useState(false);
  const [openModalOutlet, setOpenModalOutlet] = useState(false);

  const getPath = (data) => {
    const matched = SIDEBAR_URL.find((item) =>
      data.detail.roles.includes(Number(item.id)),
    );

    return matched ? matched.path : "/profile"; // fallback
  };

  const {
    mutate: mutateCheckStatusByEmail,
    isLoading: isLoadingCheckStatusByEmail,
  } = useMutation({
    mutationFn: checkStatusByEmail,
    onSuccess: (data) => {
      if (
        data.status_code === 10 ||
        data.status_code === 11 ||
        data.status_code_user === 4
      ) {
        setStatusCode(true);
        setShowPassword(false);
        setErrorMessage(data.status_user);
      } else {
        setStatusCode(false);
        setShowPassword(true);
      }
    },
    onError: (err) => {
      setStatusCode(true);
      setErrorMessage(err.message.id);
    },
  });

  const {
    mutate: mutateLogin,
    isLoading: isLoginLoading,
    isError: isLoginError,
    data: dataLogin,
  } = useMutation({
    mutationFn: login,
    onSuccess: (data) => {
      if (data.detail === undefined) return;
      dispatch(
        setUser({
          accessTokenOfficeOlin: data.detail.token.access,
          accessExpired: data.detail.token.access_expired,
          renewTokenOfficeOlin: data.detail.token.renew,
          renewExpired: data.detail.token.renew_expired,
          img_profile: data.detail.foto_profile,
          name: data.detail.nama_lengkap,
          company_name: data.detail.company_name,
          job: data.detail.title_name,
          title: data.detail.title,
          company_id: data.detail.company_id,
          roles: data.detail.roles,
          package: data.detail.package,
          status_code_user: data.detail.status_code_user,
          status_code: data.detail.status_code,
          is_tax_free: data.detail.is_tax_free,
          is_psef: data.detail.is_psef,
        }),
      );

      if (data.detail.outlets && data.detail.outlets.length > 1)
        setOpenModalOutlet(true);
      if (data.detail.outlets && data.detail.outlets.length === 1)
        if (Cookies.get("accessTokenOfficeOlin") !== undefined || null || "") {
          var path = getPath(data);
          navigate(data.detail.title === 1 ? "/purchase-pbf" : path);
        } else {
          navigate("/login");
        }
    },
    onError: (err) => {
      setErrorMessage(err.message.id);
    },
  });

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    onSubmit: (values) => {
      if (!statusCode && showPassword) mutateLogin(values);
      else {
        mutateCheckStatusByEmail({
          email: values.email,
        });
      }
    },
  });

  const handleClose = (_, reason) => {
    if (reason === "clickaway") return;
    setStatusCode(false);
    setErrorMessage("");
  };

  const sb = SendbirdChat.init({
    appId: process.env.REACT_APP_SENDBIRD_APP_ID,
    modules: [new GroupChannelModule()],
  });

  async function connectToSendbird(currentUserId, currentRole) {
    try {
      await sb.connect(currentUserId);

      const params = {
        nickname: fullname || `User ${currentUserId}`,
        profileUrl: profileImg === null ? "" : profileImg,
      };

      await sb.updateCurrentUserInfo(params);
      const data = {
        role: "seller",
      };

      await sb.currentUser.createMetaData(data);
    } catch (error) {
      console.error("Failed to connect:", error);
    }
  }

  useEffect(() => {
    const storedUserId = userId;
    const storedRole = "seller";
    if (storedUserId) {
      connectToSendbird(storedUserId, storedRole).then(() => {});
    }
  }, [userId, fullname, profileImg]);

  // Remove cookie on loading screen
  useEffect(() => {
    if (
      (pathname === "/login" || pathname === "/") &&
      !window.location.href.includes("?")
    ) {
      window.location.href = `${pathname}?nocache=${Date.now()}`;
      dispatch(logout());
    }
  }, [pathname]);

  // useEffect(() => {
  //   const form = document.querySelector("form");
  //   const handleKeyDown = (e) => {
  //     if (e.key === "Enter") {
  //       e.preventDefault();
  //     }
  //   };
  //   form.addEventListener("keydown", handleKeyDown);
  //   return () => {
  //     form.removeEventListener("keydown", handleKeyDown);
  //   };
  // }, []);

  return (
    <ThemeProvider theme={theme}>
      <div className="bg-white ">
        <div className="flex flex-col-reverse lg:flex-row justify-center min-h-screen lg:max-h-screen lg:overflow-hidden h-full">
          <div className="order-first md:order-last bg-cover hidden lg:block lg:w-2/3">
            <div className="flex items-center h-full px-20 bg-[#EAF3FE] bg-opacity-40">
              <div className="w-full my-10">
                <Carousel />
              </div>
            </div>
          </div>
          <div className="flex mt-20 w-full max-w-md px-6 mx-auto lg:w-2/6">
            <div className="flex-1">
              <div className="flex flex-col items-center justify-center text-center">
                <img
                  src={Logo}
                  alt=""
                  className="mt-16 lg:my-0 w-[100px] lg:w-[120px]"
                />
                <h2 className="text-[32px] font-bold text-center  text-black mt-3 lg:mt-20">
                  Login OLIN
                </h2>
                <p className="mt-1 text-[14px] text-gray-500 ">
                  Silakan masuk untuk mengakses akun Anda
                </p>
              </div>
              {isLoginLoading ? (
                <div className="flex justify-center items-center h-[47%] mb-[20px]">
                  <CircularProgress />
                </div>
              ) : (
                <form onSubmit={formik.handleSubmit}>
                  <div className="mt-8 w-full">
                    <Input
                      name="email"
                      label="E-mail"
                      placeholder="Example@mail.com"
                      hasPassword={false}
                      passwordVisibility={passwordVisibility}
                      setPasswordVisibility={setPasswordVisibility}
                      value={formik.values.email}
                      onChange={(e) =>
                        formik.setFieldValue("email", e.target.value)
                      }
                      disabled={!statusCode && showPassword}
                    />
                    {showPassword ? (
                      <p
                        className="text-end text-blue-400 hover:cursor-pointer text-[14px]"
                        onClick={() => {
                          setShowPassword(false);
                        }}
                      >
                        Ubah
                      </p>
                    ) : null}
                    <div>
                      <>
                        {!statusCode && showPassword ? (
                          <>
                            <div className="flex justify-between w-full items-center">
                              <Input
                                name="password"
                                label="Password"
                                hasPassword={true}
                                passwordVisibility={passwordVisibility}
                                setPasswordVisibility={setPasswordVisibility}
                                placeholder="Password anda"
                                value={formik.values.password}
                                onChange={(e) => {
                                  formik.setFieldValue(
                                    "password",
                                    e.target.value,
                                  );
                                  setErrorMessage("");
                                }}
                              />
                            </div>
                            {isLoginError ? (
                              <p className="text-sm text-red-500 text-[14px]">
                                {errorMessage}
                              </p>
                            ) : null}

                            <div>
                              <p className="text-red-500 hover:underline hover:text-red-500 cursor-pointer text-end text-sm mt-2 text-[14px]">
                                <Link to="/forgot-password">
                                  Lupa password?
                                </Link>
                              </p>
                            </div>
                          </>
                        ) : null}
                      </>
                    </div>
                    <div className="mt-6 mb-2">
                      <button
                        className="text-[14px] w-full px-4 py-2 tracking-wide text-white hover:bg-gray-400 transition-colors duration-200 transform bg-blue-500 !rounded-md  focus:outline-none focus:bg-blue-400 focus:ring focus:ring-blue-300 focus:ring-opacity-50 disabled:bg-gray-300"
                        type="submit"
                        disabled={
                          formik.values.email === "" ||
                          isLoadingCheckStatusByEmail
                        }
                      >
                        {isLoadingCheckStatusByEmail
                          ? "Loading"
                          : !statusCode && showPassword
                            ? "Sign In"
                            : "Selanjutnya"}
                      </button>
                    </div>
                    <button
                      className="text-[14px] mb-4 w-full px-4 py-2 tracking-wide text-blue-500 transition-colors duration-200 transform border-2 border-blue-500 rounded-md  focus:outline-none focus:bg-gray-100 focus:ring focus:ring-blue-300 disabled:bg-gray-300 hover:bg-gray-100"
                      type="button"
                      onClick={() => setOpenRegister(true)}
                    >
                      Registrasi
                    </button>
                    {/* <div className="relative mt-6 h-px bg-gray-300">
                    <div className="absolute left-0 top-0 flex justify-center w-full -mt-2">
                      <span className="bg-white px-4 text-xs text-gray-500 uppercase">
                        Belum Registrasi?
                      </span>
                    </div>
                  </div> */}
                    {/* <div className="mt-6">
                    <button
                      className="w-full px-4 py-2 tracking-wide text-white transition-colors duration-200 transform bg-gray-300 rounded-md focus:outline-none focus:bg-blue-400 focus:ring focus:ring-blue-300 focus:ring-opacity-50"
                      disabled
                    >
                      Daftar
                    </button>
                  </div> */}
                    <div className="flex flex-col items-center justify-center">
                      <p className="font-medium text-center text-[12px] mb-1">
                        Pelajari lebih lanjut tentang OLIN
                      </p>
                      <p className="text-[12px] border-1 text-center py-2 px-8 rounded-xl border-orange-500 w-fit text-amber-500 text-center">
                        Unduh Aplikasi
                      </p>
                    </div>
                    {/* <div className="mt-6 text-sm text-center text-gray-400">
                    Don&#x27;t have an account yet?{" "}
                    <p className="text-blue-500 underline focus:outline-none hover:underline focus:underline hover:underline">
                      Sign up
                    </p>
                    .
                  </div> */}
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
        <Snackbar
          open={statusCode}
          autoHideDuration={5000}
          onClose={() => {
            setStatusCode(false);
            setErrorMessage("");
          }}
          message={errorMessage}
          action={
            <>
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={handleClose}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </>
          }
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        />

        <RegisterModal open={openRegister} setOpen={setOpenRegister} />
        <ModalSelectOutlet
          open={openModalOutlet}
          setOpen={setOpenModalOutlet}
          dataLogin={dataLogin}
        />
      </div>
    </ThemeProvider>
  );
};

export default HOC(EmailLogin, "Login-required");
